import { Injectable } from '@angular/core';
import { TokenService, RefreshTokenService } from 'abp-ng2-module';
import { TokenAuthServiceProxy, RefreshTokenResult } from '@shared/service-proxies/service-proxies';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay } from 'rxjs/operators';
import { AppConsts } from '@shared/AppConsts';
import { LocalStorageService } from '@shared/utils/local-storage.service';

@Injectable({
  providedIn: 'root'
})
export class ZeroRefreshTokenService implements RefreshTokenService {
  /** The access token cookie expires this many seconds before the token, so an expired token is never sent. */
  static readonly accessTokenCookieMarginSeconds = 600;

  // Requests that need a refresh at the same moment share one refresh call
  private inFlightRefresh: Observable<boolean> | null = null;

  constructor(
    private _tokenAuthService: TokenAuthServiceProxy,
    private _tokenService: TokenService,
    private _localStorageService: LocalStorageService
  ) { }

  static getAccessTokenCookieExpireDate(expireInSeconds: number): Date {
    const seconds = Math.max(expireInSeconds - ZeroRefreshTokenService.accessTokenCookieMarginSeconds, expireInSeconds / 2);
    return new Date(new Date().getTime() + 1000 * seconds);
  }

  tryAuthWithRefreshToken(): Observable<boolean> {
    let token = this._tokenService.getRefreshToken();
    if (!token || token.trim() === '') {
      return of(false);
    }

    if (!this.inFlightRefresh) {
      this.inFlightRefresh = this._tokenAuthService.refreshToken(token).pipe(
        map((tokenResult: RefreshTokenResult) => {
          if (!tokenResult || !tokenResult.accessToken) {
            return false;
          }

          let tokenExpireDate = ZeroRefreshTokenService.getAccessTokenCookieExpireDate(tokenResult.expireInSeconds);
          this._tokenService.setToken(tokenResult.accessToken, tokenExpireDate);

          this._localStorageService.setItem(AppConsts.authorization.encrptedAuthTokenName,
            {
              token: tokenResult.encryptedAccessToken,
              expireDate: tokenExpireDate
            });

          return true;
        }),
        catchError(() => of(false)),
        finalize(() => this.inFlightRefresh = null),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }

    return this.inFlightRefresh;
  }
}
